import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { usePermission } from "@/auth/usePermission";
import { OrderStatusBadge } from "../components/OrderStatusBadge";
import type { FulfillmentType } from "../types";
// ⚠️ Best-guess hook names — verify against generated v1.ts (Rule MKT-FE-05).
import {
  useV1MarketplaceOrdersRetrieve,
  useV1MarketplaceOrdersAcceptCreate,
  useV1MarketplaceOrdersRejectCreate,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

export function OrderDetailPage() {
  const { id = "" } = useParams();
  const { data, refetch } = useV1MarketplaceOrdersRetrieve(id);
  const acceptMutation = useV1MarketplaceOrdersAcceptCreate();
  const rejectMutation = useV1MarketplaceOrdersRejectCreate();

  const [fulfillmentType, setFulfillmentType] =
    useState<FulfillmentType>("from_stock");
  const [rejectReason, setRejectReason] = useState("");

  // AM-010 Rule MIS-08 / ADR-069-B Rule MKT-FE-04: accept/reject are now
  // gated server-side by marketplace.manage. The buttons must be HIDDEN
  // (not just disabled) for users without it, or they'll get a fresh 403
  // that never used to happen before this AM.
  const canManage = usePermission("marketplace.manage");

  const order = data;
  if (!order) return <div style={{ padding: "2rem" }}>در حال بارگذاری...</div>;

  const canAct = canManage && order.status === "created";

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={`سفارش ${order.id}`}
        subtitle={`مشاهده جزئیات سفارش و عملیات مدیریتی`}
        icon="📦"
        helpLink="/help/marketplace/order-detail"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <Link
            to="/marketplace/orders"
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              textDecoration: "none",
            }}
          >
            ← بازگشت
          </Link>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ color: "var(--color-text-muted)" }}>وضعیت:</span>
            <OrderStatusBadge status={order.status} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ color: "var(--color-text-muted)" }}>مقدار سفارش:</span>
            <span style={{ fontWeight: "bold" }}>{order.quantity}</span>
          </div>
        </div>

        {canAct && (
          <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", padding: "1.5rem", background: "rgba(255,255,255,0.02)", borderRadius: "12px", border: "1px solid var(--glass-border)" }}>
            <h3 style={{ margin: 0, fontSize: "1.1rem" }}>عملیات سفارش</h3>
            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "200px" }}>
                <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>نوع تأمین</label>
                <select
                  className="input-field"
                  value={fulfillmentType}
                  onChange={(e) => setFulfillmentType(e.target.value as FulfillmentType)}
                  style={{ width: "100%" }}
                >
                  <option value="from_stock">از موجودی</option>
                  <option value="make_to_order">تولید سفارشی</option>
                </select>
              </div>
              <button
                className="btn-primary"
                onClick={() =>
                  acceptMutation.mutate({ id: id, data: { fulfillment_type: fulfillmentType } as any },
                    { onSuccess: () => refetch() }
                  )
                }
                disabled={acceptMutation.isPending}
              >
                {acceptMutation.isPending ? "در حال پردازش..." : "پذیرش سفارش"}
              </button>
            </div>

            <div style={{ display: "flex", gap: "1rem", alignItems: "flex-end", flexWrap: "wrap", borderTop: "1px solid var(--glass-border)", paddingTop: "1.5rem" }}>
              <div style={{ flex: 1, minWidth: "200px" }}>
                <label style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.85rem", color: "var(--color-text-muted)" }}>دلیل رد (اختیاری)</label>
                <input
                  className="input-field"
                  placeholder="توضیحات خود را بنویسید..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  style={{ width: "100%" }}
                />
              </div>
              <button
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ff4d4d",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: rejectMutation.isPending ? "not-allowed" : "pointer",
                  opacity: rejectMutation.isPending ? 0.7 : 1,
                }}
                onClick={() =>
                  rejectMutation.mutate({ id: id, data: { reason: rejectReason } as any },
                    { onSuccess: () => refetch() }
                  )
                }
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending ? "در حال پردازش..." : "رد سفارش"}
              </button>
            </div>
          </div>
        )}

        {!canManage && order.status === "created" && (
          <p style={{ color: "var(--color-text-muted)", fontSize: "0.9rem", fontStyle: "italic", padding: "1rem", background: "rgba(0,0,0,0.05)", borderRadius: "8px" }}>
            برای پذیرش/رد این سفارش به مجوز marketplace.manage نیاز دارید.
          </p>
        )}
      </div>
    </div>
  );
}
